import { useState } from 'react';
import { DataTable, StatusField, Pagination, Icon, EmptyState, useSpinner } from '@chikara/ui';
import { DASHBOARD_MOVEMENT_COLUMNS, DASHBOARD_MOVEMENT_MOBILE_CONFIG, DASHBOARD_ITEMS_PER_PAGE } from '@/features/dashboard/constants/dashboard.constants';
import { Transaction } from '@/features/card/types/account.types';
import { formatMXN } from '@/features/dashboard/utils/dashboard.utils';
import emptyListIcon from '@/features/dashboard/assets/empty-list-icon.svg';
import { ReportChargebackModal } from '@/features/dashboard/components/ReportChargebackModal/ReportChargebackModal';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import styles from './DashboardMovements.module.css';
import { t } from '@/i18n';


interface DashboardMovementsProps {
  data: Transaction[];
  total: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export function DashboardMovements({ data, total, currentPage, onPageChange }: DashboardMovementsProps) {
  const { userData } = useAuth();
  const { isSpinnerLoading } = useSpinner();
  const basic = isBasic(userData.role);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const totalPages = Math.ceil(total / DASHBOARD_ITEMS_PER_PAGE);

  const renderers: Record<string, (value: any, row: any) => React.ReactNode> = {
    amount: (value) => {
      const amount = typeof value === 'number' ? value : parseFloat(value as string);
      const formattedAmount = formatMXN(Math.abs(amount));
      return <span>{formattedAmount}</span>;
    },
    cdate: (value) => {
      const dateStr = value as string;
      if (!dateStr) return value;
      const [date, time] = dateStr.split(', ');
      return (
        <div className={styles.dateContainer}>
          <span className={styles.datePart}>{date}</span>
          {time && <span className={styles.datePart}>{time}</span>}
        </div>
      );
    },
    movement: (value, row) => {
      const isNegative = row.type === 'Abono';
      const isRejected = row.status === 'declined';
      const mov = value as any;

      const getIconName = () => {
        if (isRejected) return 'transaction-rejected';
        return isNegative ? 'transaction-out' : 'transaction-in';
      };

      const getIconContainerStyle = () => {
        if (isRejected) return styles.iconContainerRejected;
        return isNegative ? styles.iconContainerOut : styles.iconContainerIn;
      };

      return (
        <div className={styles.movementContainer}>
          <div className={getIconContainerStyle()}>
            <Icon
              name={getIconName()}
              size='2xl'
              color='inherit'
            />
          </div>

          <div className={styles.movementCell}>
            <div className={styles.movementDescription}>{mov.description}</div>
            <div className={styles.movementDetails}>
              {mov.subcategory}
            </div>
          </div>
        </div>
      );
    },
    status: (value, row) => (
      <StatusField
        status={row.statusName as string}
        statusType={value as any}
        actionLabel={row.report ? t('dashboard.reportAction') : undefined}
        showReportIcon={row.report}
        onActionClick={() => {
          setSelectedTransaction(row);
          setIsModalOpen(true);
        }}
        reportTooltip={row.report ? t('dashboard.reportTooltip') : undefined}
      />
    ),
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{basic ? t('dashboard.userMovementsTitle') : t('dashboard.teamMovementsTitle')}</h2>
      {(data?.length === 0 && !isSpinnerLoading) || !data ? (
        <EmptyState
          title={t('dashboard.emptyState.title')}
          description={basic ? t('dashboard.emptyState.descriptionBasicRole') : t('dashboard.emptyState.description')}
          imgSrc={emptyListIcon}
          maxWidth='460px'
        />
      ) : (
        <DataTable
          columns={DASHBOARD_MOVEMENT_COLUMNS}
          data={data}
          mobileConfig={DASHBOARD_MOVEMENT_MOBILE_CONFIG}
          isLoading={isSpinnerLoading}
          renderCell={(key, value, row) => {
            const renderer = renderers[key];
            return renderer ? renderer(value, row) : value;
          }}
        />
      )}
      {total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          showInfo
          infoText={t('dashboard.paginationInfo', {
            start: (currentPage - 1) * DASHBOARD_ITEMS_PER_PAGE + 1,
            end: Math.min(currentPage * DASHBOARD_ITEMS_PER_PAGE, total),
            total: total
          })}
        />
      )}
      <ReportChargebackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={selectedTransaction}
      />
    </section>
  );
}
